import { Select } from '@profgeosoft-ui/react';
import clsx from 'clsx';
import { reaction } from 'mobx';
import { useCallback, useEffect, useState, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { ComboboxDropdown } from 'src/components/combobox-dropdown';
import { wrap } from 'src/packages/mobx-di/wrap';
import { hasValue } from 'src/packages/utils/has-value';

import type { RefSelectProps } from '@profgeosoft-ui/react';
import type { TOption } from 'src/packages/types';
import type { OptionsService } from 'src/services/directory-service/options-service';
import type { TComboBoxView, TFieldRulesData } from 'src/services/directory-service/types';

import styles from './combobox.module.scss';

type Props = {
  controlView: TComboBoxView;
  optionsService: OptionsService;
  className?: string;
  value: number | string | null;
  error?: string;
  fieldRulesData?: TFieldRulesData;
  innerRef?: MutableRefObject<RefSelectProps | null>;
  loadOptionsFn: (signal?: AbortSignal) => Promise<TOption[]>;
  onChange(fieldId: string, value: unknown): void;
  onBlur?(fieldId: string): void;
  onCreateNewRecord?(): void;
};

export const ComboboxComponent = wrap<Props>(function ComboboxComponent({
  innerRef,
  controlView,
  optionsService,
  className,
  value,
  error,
  fieldRulesData,
  onCreateNewRecord,
  onChange,
  loadOptionsFn,
  onBlur,
}) {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [internalOptions, setInternalOptions] = useState<TOption[]>(optionsService.options[controlView.fieldId] ?? []);
  const [isLoading, setIsLoading] = useState(false);

  const actualOption = hasValue(value) ? optionsService.optionsMap[controlView.fieldId].get(value) : null;

  const optionAsValue = (() => {
    if (actualOption) {
      return actualOption;
    }

    if (hasValue(value)) {
      return optionsService.archivedOptionsMap[controlView.fieldId]?.get(value) ?? null;
    }

    return null;
  })();

  const loadOptions = useCallback((): VoidFunction => {
    const abortController = new AbortController();

    const load = async () => {
      setIsLoading(true);
      const options = await loadOptionsFn(abortController.signal);
      setIsLoading(false);
      setInternalOptions(options);
    };

    load();

    return () => abortController.abort();
  }, [loadOptionsFn]);

  const handleChange = (value: number | null | TOption) => {
    if (!hasValue(value)) {
      onChange(controlView.fieldId, null);
      return;
    }

    if (typeof value === 'number') {
      onChange(controlView.fieldId, value);
      return;
    }

    onChange(controlView.fieldId, value.value);
  };

  const handleBlur = () => {
    onBlur?.(controlView.fieldId);
  };

  useEffect(() => {
    if (!controlView.refQuery) {
      return;
    }

    return loadOptions();
  }, [controlView.refQuery, loadOptions]);

  useEffect(() => {
    return reaction(
      () => optionsService.options[controlView.fieldId] ?? [],
      (options) => setInternalOptions(options),
      { fireImmediately: true },
    );
  }, [controlView.fieldId, optionsService.options]);

  const isOpened =
    isOpen &&
    !fieldRulesData?.isLoading &&
    !isLoading &&
    !fieldRulesData?.isDisabled &&
    !fieldRulesData?.isEnteringBlocked;

  return (
    <Select<TOption | null>
      ref={innerRef}
      open={isOpened}
      value={optionAsValue}
      options={internalOptions}
      dropdownMatchSelectWidth={false}
      className={clsx(styles.combobox, className)}
      allowClear
      sortOptions
      loading={fieldRulesData?.isLoading || isLoading}
      status={error ? 'error' : undefined}
      optionFilterProp="label"
      showSearch
      disabled={fieldRulesData?.isDisabled || fieldRulesData?.isEnteringBlocked}
      placeholder={t('common:placeholders.chooseValue')}
      onDropdownVisibleChange={(open) => setIsOpen(open)}
      dropdownRender={
        onCreateNewRecord
          ? (menu) => (
              <ComboboxDropdown onButtonClick={onCreateNewRecord} onClose={() => setIsOpen(false)}>
                {menu}
              </ComboboxDropdown>
            )
          : undefined
      }
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
});
